/**
 * @file taxonomy-tabs.js
 * Behaviour: kilden/taxonomy-tabs
 *
 * Responsibilities
 * 1. Detect which tab panels have actual content (non-empty inner HTML).
 * 2. Disable tabs whose panels are empty.
 * 3. Auto-activate the first enabled tab.
 * 4. Handle keyboard navigation (←/→ arrows, Home, End) per WAI-ARIA
 *    Tabs pattern.
 */
(function (Drupal) {
  'use strict';

  Drupal.behaviors.kildenTaxonomyTabs = {
    attach(context) {
      const components = once(
        'kilden-taxonomy-tabs',
        '.taxonomy-tabs[data-component-id="kilden:taxonomy-tabs"]',
        context
      );

      components.forEach((component) => {
        const tabs   = Array.from(component.querySelectorAll('.taxonomy-tabs__tab'));
        const panels = Array.from(component.querySelectorAll('.taxonomy-tabs__panel'));

        /**
         * Check whether a panel slot contains real content.
         * A Views block that returns 0 rows renders only whitespace / empty
         * wrapper divs. We consider a panel "empty" when its innerText
         * (trimmed) is blank OR when it only contains the empty-state
         * class Drupal Views adds (.view-empty).
         */
        function hasContent(panel) {
          const inner = panel.querySelector('[data-slot-content]');
          if (!inner) return false;
          if (inner.querySelector('.view-empty')) return false;
          // Only count as having content if there's an actual view-content div
          if (!inner.querySelector('.view-content')) return false;
          return true;
        }

        // Build a map of tabId → hasContent
        const contentMap = {};
        panels.forEach((panel) => {
          const id = panel.dataset.panel;
          contentMap[id] = hasContent(panel);
        });

        // Mark disabled tabs
        tabs.forEach((tab) => {
          if (!contentMap[tab.dataset.tab]) {
            tab.setAttribute('aria-disabled', 'true');
            tab.setAttribute('tabindex', '-1');
          }
        });

        // Activate a tab
        function activate(tab) {
          // Deactivate all
          tabs.forEach((t) => {
            t.setAttribute('aria-selected', 'false');
            t.setAttribute('tabindex', '-1');
          });
          panels.forEach((p) => p.setAttribute('hidden', ''));

          // Activate target
          tab.setAttribute('aria-selected', 'true');
          tab.removeAttribute('tabindex');

          const targetPanel = component.querySelector(
            `#panel-${tab.dataset.tab}`
          );
          if (targetPanel) targetPanel.removeAttribute('hidden');
        }

        // Auto-activate first tab that has content
        const firstEnabled = tabs.find((t) => !contentMap[t.dataset.tab] === false
          && t.getAttribute('aria-disabled') !== 'true');
        if (firstEnabled) activate(firstEnabled);

        // Click handler
        tabs.forEach((tab) => {
          tab.addEventListener('click', () => {
            if (tab.getAttribute('aria-disabled') === 'true') return;
            activate(tab);
            tab.focus();
          });
        });

        // Keyboard navigation (WAI-ARIA roving tabindex)
        tabs.forEach((tab, index) => {
          tab.addEventListener('keydown', (e) => {
            let newIndex = null;
            const enabledTabs = tabs.filter(
              (t) => t.getAttribute('aria-disabled') !== 'true'
            );
            const currentEnabledIndex = enabledTabs.indexOf(tab);

            if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
              newIndex = (currentEnabledIndex + 1) % enabledTabs.length;
            } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
              newIndex =
                (currentEnabledIndex - 1 + enabledTabs.length) %
                enabledTabs.length;
            } else if (e.key === 'Home') {
              newIndex = 0;
            } else if (e.key === 'End') {
              newIndex = enabledTabs.length - 1;
            }

            if (newIndex !== null) {
              e.preventDefault();
              activate(enabledTabs[newIndex]);
              enabledTabs[newIndex].focus();
            }
          });
        });
      });
    },
  };
})(Drupal);
